import {
  convertChildrenToSentenceCase,
  sentenceCase,
} from '@cabonline/ui-shared';
import { ReactElement } from 'react';
import {
  CSSObjectWithLabel,
  GroupBase,
  InputProps,
  OptionProps,
  SingleValueProps,
  components,
} from 'react-select';

import { StyledIconWrapper, StyledValueWrapper } from './styles';

export interface CustomStyles {
  control?: CSSObjectWithLabel;
  option?: CSSObjectWithLabel;
  singleValue?: CSSObjectWithLabel;
  menu?: CSSObjectWithLabel;
  menuList?: CSSObjectWithLabel;
}

export interface OwnProps {
  id: string;
  label?: string;
  help?: string;
  hasError?: boolean | string;
  isRequired?: boolean;
  showSeparator?: boolean;
  hideCaret?: boolean;
  darkMode?: boolean;
  prefixIcon?: ReactElement;
  testID?: string;
  sentenceCased?: boolean;
  stopPropagation?: boolean;
  customStyles?: CustomStyles;
}

export const CustomInput = <
  Option,
  Group extends GroupBase<Option>,
  isMulti extends boolean
>(
  props: InputProps<Option, isMulti, Group>
) => <components.Input {...props} autoComplete="off" />;

interface CustomSingleValueProps<Option> extends SingleValueProps<Option> {
  prefixIcon?: ReactElement;
  testID?: string;
  sentenceCased?: boolean;
}

export const CustomSingleValue = <Option,>(
  props: CustomSingleValueProps<Option>
) => {
  const children = props.children;
  return (
    <components.SingleValue {...props}>
      <StyledValueWrapper>
        {props.prefixIcon && (
          <StyledIconWrapper>{props.prefixIcon}</StyledIconWrapper>
        )}
        {convertChildrenToSentenceCase(children, props.sentenceCased)}
      </StyledValueWrapper>
    </components.SingleValue>
  );
};

export const CustomOption = <
  Option extends object,
  Group extends GroupBase<Option>,
  isMulti extends boolean
>(
  optionProps: OptionProps<Option, isMulti, Group>,
  testID: string | undefined,
  sentenceCased: boolean | undefined
) => {
  const serializedTestValue =
    'value' in optionProps.data && typeof optionProps.data.value === 'string'
      ? optionProps.data.value
      : optionProps.label
          .toLowerCase()
          .replace(/[^\w\såäöæø]+/g, '')
          .replace(/\s+/g, '-');

  const innerPropsWithTestId = {
    ...optionProps.innerProps,
    'data-testid': `${testID}-${serializedTestValue}`,
  } as React.HTMLProps<HTMLDivElement>;
  return (
    <components.Option {...optionProps} innerProps={innerPropsWithTestId}>
      {sentenceCased ? sentenceCase(optionProps.label) : optionProps.label}
    </components.Option>
  );
};
